import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  AmountInput,
  Button,
  CurrenciesUSD,
  Dropdown,
  Icon,
  Input,
  Toggle,
  Tooltip,
  formatNumber,
} from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import {
  BankAccountCard,
  CopyInput,
  DateInput,
  Details,
  EntityCard,
  RenderFields,
  RestrictedBanner,
} from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { EntityRepository, Loan, LoanRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { EditToolbar, Inner } from '~/styles';
import { UpdateLoanRequest } from '~/typings/API';
import { formatDateTime } from '~/util';

import { LoanBalancesBreakdown } from './_components/LoanBalancesBreakdown';
import { LoanStatusIcon } from './_components/LoanStatusIcon';
import { LoanProps } from './Loan';

interface Params {
  id: string;
}

interface LoanForm {
  description: string;
  loanId?: string;
  balances?: Loan['balances'];
  isRevolving: boolean;
  maturityDate?: string;
  maxPrincipalBalance?: number;
  currency?: string;
  status?: string;
  primarySignerEntityId?: string;
  entityName?: string;
  collateralBankAccountId?: string;
  collateralLockedAmount?: number;
  createdAt?: string;
  chargedOffAt?: string;
  paidOffAt?: string;
  delinquentAt?: string;
  disputedAt?: string;
}

interface UpdateLoanRequestLocal extends UpdateLoanRequest {
  status?: string;
}

const loanQueryKey = (id: string) => ['loan', id] as const;
const entityQueryKey = (platformId: string | undefined, id: string) => [platformId, 'entity', id] as const;

const ToggleWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: 40px;

  svg {
    --icon-size: 18px;
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  }
`;

export const PageLoanInformation: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<keyof Params>() as Params;
  const { loan: outletLoan } = useOutletContext<LoanProps>();
  const { setValue, watch, reset, getValues, handleSubmit, formState } = useFormContext<LoanForm>();
  const { handleSubmitErrors } = useFormErrorHandler();

  const { data: loan, error: loanError } = useQuery({
    queryKey: loanQueryKey(id!),
    queryFn: () => LoanRepository.get(id!),
    enabled: !!id && !outletLoan,
    initialData: outletLoan,
  });

  const primarySignerEntityId = loan?.primarySignerEntityId;
  const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId;

  const { data: entity, error: entityError } = useQuery({
    queryKey: entityQueryKey(platformId, primarySignerEntityId!),
    queryFn: () => EntityRepository.get({ id: primarySignerEntityId! }),
    enabled: !!primarySignerEntityId,
  });

  const updateFormValues = useCallback(
    (loanData: Loan) => {
      setValue('description', loanData.description ?? '');
      setValue('loanId', loanData.id);
      setValue('isRevolving', Boolean(loanData.isRevolving));
      setValue('maturityDate', loanData.maturityDate ?? undefined);
      setValue(
        'maxPrincipalBalance',
        loanData.maxPrincipalBalance ? parseInt(loanData.maxPrincipalBalance, 10) : undefined
      );
      if (loanData.status) {
        setValue('status', loanData.status);
      }
      setValue('createdAt', loanData.createdAt ?? undefined);
      setValue('chargedOffAt', loanData.chargedOffAt ?? undefined);
      setValue('paidOffAt', loanData.paidOffAt ?? undefined);
      setValue('delinquentAt', loanData.delinquentAt ?? undefined);
      setValue('disputedAt', loanData.disputedAt ?? undefined);
      setValue('primarySignerEntityId', loanData.primarySignerEntityId ?? '');
    },
    [setValue]
  );

  const updateLoanMutation = useMutation({
    mutationFn: (request: Partial<UpdateLoanRequest>) => {
      if (!id) throw new Error('Loan ID is required');
      return LoanRepository.update(id, request);
    },
    onSuccess: (response: Loan) => {
      queryClient.setQueryData(loanQueryKey(id!), response);
      if (response.primarySignerEntityId !== primarySignerEntityId) {
        queryClient.invalidateQueries({ queryKey: entityQueryKey(platformId, response.primarySignerEntityId) });
      }
      updateFormValues(response);
      addSuccessNotification({
        content: `${response.description} updated`,
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message,
      });
    },
  });

  useEffect(() => {
    if (!entity) {
      if (!primarySignerEntityId) {
        setValue('entityName', '');
      }
      return;
    }
    if ('personDetails' in entity && entity.personDetails) {
      setValue('entityName', `${entity.personDetails.firstName} ${entity.personDetails.lastName}`);
    } else if ('businessDetails' in entity && entity.businessDetails?.businessName) {
      setValue('entityName', entity.businessDetails.businessName);
    }
  }, [entity, primarySignerEntityId, setValue]);

  const onSubmit = useCallback(
    (data: LoanForm) => {
      if (!id) return;
      const request: Partial<UpdateLoanRequestLocal> = {
        description: data.description,
        isRevolving: data.isRevolving,
        maturityDate: data.maturityDate,
        maxPrincipalBalance: data.maxPrincipalBalance != null ? data.maxPrincipalBalance.toString() : undefined,
        status: data.status,
      };
      updateLoanMutation.mutate(request as Partial<UpdateLoanRequest>);
    },
    [id, updateLoanMutation]
  );

  useEffect(() => {
    if (loanError) {
      navigate(ROUTE.LOANS);
      addDangerNotification({
        content: (loanError as any).message || 'Failed to load loan',
      });
    }
  }, [loanError, navigate, addDangerNotification]);

  useEffect(() => {
    if (entityError) {
      addDangerNotification({
        content: (entityError as any).message || 'Failed to load entity information',
      });
    }
  }, [entityError, addDangerNotification]);

  useEffect(() => {
    if (!loan) return;
    const existingEntityName = getValues('entityName');
    reset({
      description: loan.description ?? '',
      loanId: loan.id,
      isRevolving: loan.isRevolving ?? false,
      maturityDate: loan.maturityDate ?? undefined,
      maxPrincipalBalance: loan.maxPrincipalBalance ? parseInt(loan.maxPrincipalBalance, 10) : undefined,
      currency: loan.currency ?? 'USD',
      status: loan.status ?? 'current',
      primarySignerEntityId: loan.primarySignerEntityId ?? '',
      createdAt: loan.createdAt ?? undefined,
      chargedOffAt: loan.chargedOffAt ?? undefined,
      paidOffAt: loan.paidOffAt ?? undefined,
      delinquentAt: loan.delinquentAt ?? undefined,
      disputedAt: loan.disputedAt ?? undefined,
      entityName: loan.primarySignerEntityId ? existingEntityName : '',
    });
    updateFormValues(loan);
  }, [loan, reset, getValues, updateFormValues]);

  const formattedLoan = useMemo(() => (loan ? (loan as unknown as Record<string, unknown>) : undefined), [loan]);

  const statusOptions = useMemo(
    () => [
      { value: 'current', label: 'Current', icon: <LoanStatusIcon $status="current" /> },
      { value: 'delinquent', label: 'Delinquent', icon: <LoanStatusIcon $status="delinquent" /> },
      { value: 'charged_off', label: 'Charged off', icon: <LoanStatusIcon $status="charged_off" /> },
      { value: 'in_dispute', label: 'In Dispute', icon: <LoanStatusIcon $status="in_dispute" /> },
      { value: 'canceled', label: 'Canceled', icon: <LoanStatusIcon $status="canceled" /> },
      { value: 'paid_off', label: 'Paid off', icon: <LoanStatusIcon $status="paid_off" /> },
    ],
    []
  );

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isSandbox: state.isSandbox,
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        () => {
          if (!currentUser) {
            return;
          }
          if (id) {
            queryClient.invalidateQueries({ queryKey: loanQueryKey(id) });
          }
        }
      ),
    [id, currentUser, queryClient]
  );

  return (
    <>
      {!isAuthorized({
        permission: 'loans',
        permissionLevel: 'write',
      }) && (
        <Inner pb={0}>
          <RestrictedBanner />
        </Inner>
      )}

      <form id="loan-information-form" autoComplete="off" onSubmit={handleSubmit(onSubmit, handleSubmitErrors)}>
        <Inner p={0} data-disabled={!isAuthorized({ permission: 'loans', permissionLevel: 'write' })}>
          {RenderFields<LoanForm>({
            sections: [
              {
                headline: 'Information',
                fields: [
                  {
                    id: 'description',
                    label: 'Description',
                    children: ({ value, onChange }) => (
                      <Input value={String(value ?? '')} onChange={onChange} placeholder="Description" />
                    ),
                  },
                  {
                    id: 'loanId',
                    label: 'ID',
                    children: ({ value }) => <CopyInput value={String(value ?? '')} isMonospaced fullWidth />,
                  },
                  {
                    id: 'primarySignerEntityId',
                    label: 'Entity',
                    hide: !id,
                    children: ({ value }) => <EntityCard entityId={String(value ?? '')} />,
                  },
                  {
                    id: 'balances',
                    label: 'Balances',
                    children: () => (
                      <LoanBalancesBreakdown balances={loan?.balances} currencyCode={loan?.currency || 'USD'} />
                    ),
                  },
                  {
                    id: 'collateralBankAccountId',
                    label: 'Collateral Account',
                    hide: loan?.type !== 'secured' || !loan.collateralBankAccountId,
                    children: () =>
                      loan?.collateralBankAccountId ? (
                        <BankAccountCard bankAccountId={loan.collateralBankAccountId} />
                      ) : null,
                  },
                  {
                    id: 'collateralLockedAmount',
                    label: 'Collateral Locked',
                    hide: loan?.type !== 'secured',
                    children: () => (
                      <CopyInput value={formatNumber(loan?.collateralLockedAmount ?? 0, loan?.currency || 'USD')} />
                    ),
                  },
                  {
                    id: 'status',
                    label: 'Status',
                    children: ({ value, onChange }) => (
                      <Dropdown
                        fullWidth
                        active={value ?? 'current'}
                        onChange={(newValue) => onChange(newValue)}
                        options={statusOptions}
                      />
                    ),
                  },
                  {
                    id: 'maxPrincipalBalance',
                    label: 'Max Principal Balance',
                    children: ({ value, onChange }) => (
                      <AmountInput
                        onChange={(v?: number) => onChange(v)}
                        onCurrencyCodeChange={(v: string) => setValue('currency', v)}
                        currencyCode={watch('currency')}
                        currencyList={CurrenciesUSD}
                        value={typeof value === 'number' ? value : value ? parseInt(String(value), 10) : undefined}
                      />
                    ),
                  },
                  {
                    id: 'maturityDate',
                    label: 'Maturity Date',
                    children: ({ value, onChange }) => (
                      <DateInput
                        onChange={(v?: string) => onChange(v)}
                        value={typeof value === 'string' ? value : undefined}
                      />
                    ),
                  },
                  {
                    id: 'isRevolving',
                    label: 'Is Revolving?',
                    children: ({ value, onChange }) => (
                      <ToggleWrapper>
                        <Toggle isChecked={!!value} onCheckedChange={onChange} />
                        <Tooltip content="Indicates whether or not the loan is revolving. If checked the loan can have multiple disbursements.">
                          <Icon.CircleQuestionmark />
                        </Tooltip>
                      </ToggleWrapper>
                    ),
                  },
                  ...(
                    [
                      { label: 'Created at', key: 'createdAt' },
                      { label: 'Charged off at', key: 'chargedOffAt' },
                      { label: 'Paid off at', key: 'paidOffAt' },
                      { label: 'Delinquent at', key: 'delinquentAt' },
                      { label: 'Disputed at', key: 'disputedAt' },
                    ] as readonly { label: string; key: keyof LoanForm }[]
                  ).map((o) => {
                    const formValues = watch();
                    const value = formValues[o.key];
                    const currentStatus = formValues.status;
                    const showForStatus =
                      o.key === 'createdAt'
                        ? true
                        : (currentStatus === 'charged_off' && o.key === 'chargedOffAt') ||
                          (currentStatus === 'paid_off' && o.key === 'paidOffAt') ||
                          (currentStatus === 'delinquent' && o.key === 'delinquentAt') ||
                          (currentStatus === 'in_dispute' && o.key === 'disputedAt');
                    return {
                      id: o.key,
                      label: o.label,
                      hide: !showForStatus || !value,
                      children: () => (
                        <CopyInput value={formatDateTime(value as unknown as string, { timeZoneFormat: 'utc' })} />
                      ),
                    };
                  }),
                ],
              },
            ],
          })}
          <Details
            label="Loan Details"
            formattedData={formattedLoan}
            rawData={loan as unknown as Record<string, unknown> | undefined}
          />
          <Inner pt={0} px="var(--page-margin)">
            <EditToolbar>
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={() => {
                  if (!loan) return;
                  const existingEntityName = getValues('entityName');
                  reset({
                    description: loan.description ?? '',
                    loanId: loan.id,
                    isRevolving: loan.isRevolving ?? false,
                    maturityDate: loan.maturityDate ?? undefined,
                    maxPrincipalBalance: loan.maxPrincipalBalance ? parseInt(loan.maxPrincipalBalance, 10) : undefined,
                    currency: loan.currency ?? 'USD',
                    status: loan.status ?? 'current',
                    primarySignerEntityId: loan.primarySignerEntityId ?? '',
                    createdAt: loan.createdAt ?? undefined,
                    chargedOffAt: loan.chargedOffAt ?? undefined,
                    paidOffAt: loan.paidOffAt ?? undefined,
                    delinquentAt: loan.delinquentAt ?? undefined,
                    disputedAt: loan.disputedAt ?? undefined,
                    entityName: loan.primarySignerEntityId ? existingEntityName : '',
                  });
                }}
                isDisabled={!formState.isDirty || !isAuthorized({ permission: 'loans', permissionLevel: 'write' })}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isDisabled={
                  !formState.isDirty ||
                  !isAuthorized({
                    permission: 'loans',
                    permissionLevel: 'write',
                  })
                }
              >
                Save Changes
              </Button>
            </EditToolbar>
          </Inner>
        </Inner>
      </form>
    </>
  );
};
